<template>
  <responsive-svg class="simulator-chart" padding="20" :padding-left="42" :padding-bottom="(3 - data.length) * 45 + 20" v-slot="contentBox">
    <user-space :layout="layout" v-bind="contentBox" v-slot="{xRange, yRange, transform}">
      <bar-plot
        :data-view="dataView"
        :x-scale="getXScale(xRange)"
        :y-scale="getYScale(yRange)"
        :animation-duration="-1">
        <template v-slot="{getGeom, hasGeom}">
          <animated-group :watching="dataView" :enter="{y: getYScale(yRange)(0), height: 0}">
            <rect v-for="d in dataView" :key="d.x" v-if="hasGeom(d)"
              class="mini-bar"
              :class="{oversubscribed: d.y2 > d.y}"
              v-animated="getMiniBarGeom(d, getGeom)">
            </rect>
          </animated-group>
        </template>
      </bar-plot>
      <y-gridlines
        :interval="yInterval"
        :x-scale="getXScale(xRange)"
        :y-scale="getYScale(yRange)">
      </y-gridlines>
      <y-axis
        :interval="yInterval"
        :x-translate="xRange[1]"
        :y-scale="getYScale(yRange)"
        anchor="top"
        :post-transform="transform">
      </y-axis>
      <x-axis
        :interval="xInterval"
        :x-scale="getXScale(xRange)"
        anchor="right"
        :post-transform="transform">
      </x-axis>
    </user-space>
  </responsive-svg>
</template>

<script>
import ResponsiveSvg from 'vue-graphics/hocs/ResponsiveSvg'
import UserSpace from 'vue-graphics/hocs/UserSpace'
import BarPlot from 'vue-graphics/components/BarPlot.vue'
import XAxis from 'vue-graphics/components/XAxis.vue'
import YAxis from 'vue-graphics/components/YAxis.vue'
import YGridlines from 'vue-graphics/components/YGridlines'

import AnimatedGroup from 'vue-graphics/animation/hocs/AnimatedGroup'
import Animated from 'vue-graphics/animation/directives/v-animated'

import {dataViewMixin} from 'vue-graphics/mixins'
import {DomainHelper, IntervalHelper, TransformHelper} from 'vue-graphics/helpers'

import {scaleBand, scaleLinear} from 'd3-scale'

export default {
  mixins: [dataViewMixin],
  components: {
    ResponsiveSvg,
    UserSpace,
    BarPlot,
    XAxis,
    YAxis,
    YGridlines,
    AnimatedGroup
  },
  directives: {Animated},
  inheritAttrs: false,
  computed: {
    layout () {
      return new TransformHelper().invert()
    }
  },
  methods: {
    x: d => d.year,
    y: d => d.vacancies,
    y2: d => d.applicants,
    xDomain: DomainHelper.UNIQUE('x'),
    yDomain: DomainHelper.CLAMPED_ROUNDED_MINMAX(['y', 'y2'], 10, 0),
    xInterval: IntervalHelper.MIDDLE(),
    yInterval: IntervalHelper.TICKS(4),
    getXScale (xRange) {
      return scaleBand()
        .domain(this.domain.x)
        .rangeRound(xRange)
        .paddingInner(0.25)
        .paddingOuter(0.4)
    },
    getYScale (yRange) {
      return scaleLinear()
       .domain(this.domain.y)
       .rangeRound(yRange)
    },
    getMiniBarGeom (d, getGeom) {
      const g = getGeom(d)
      const height = d.y2 / d.y * g.height
      const y = g.y + g.height - height
      return {
        x: g.x + 0.3 * g.width,
        y,
        width: 0.4 * g.width,
        height
      }
    }
  }
}
</script>

<style lang="scss">
.simulator-chart {
  width: 100%;
  height: 180px;

  .vg-x-axis .vg-baseline {
    stroke: $black;
  }

  .vg-y-axis .vg-baseline {
    stroke: none;
  }

  .vg-tick > line {
    display: none;
  }

  .vg-tick > text {
    fill: $black;
  }

  .vg-gridlines line {
    stroke: $black;
    stroke-dasharray: 1px;
  }

  .vg-bar {
    fill: rgba($themeColor,20%);
    opacity: 1;
  }

  .mini-bar {
    fill: $themeColor;
    opacity: 1;
    transition: fill 0.5s;
  }

  .mini-bar.oversubscribed {
    fill: $red;
  }
}
</style>
